import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Icon, Loader } from "@atoms";
import { useModal } from "@hooks";
import {
  getDefaultBuilding,
  getDefaultBuildingId,
  getIsParkingFeatureVisibleForBuilding,
  getIsUserAllowedToReserveParking,
  getLicensePlates,
  getParkingLotById,
  getParkingReservationByDate,
  getParkingSettings,
  getPreferences,
  reserveParkingSpotWithPreferredLocation,
  useAppDispatch,
  useAppSelector,
  useOverridableFeatureFlags,
  withAsyncThunkErrorHandling,
  getIsNodeClosed,
  selectBuilding,
} from "@lib/store";
import { actions } from "@lib/store/src/data/timeslots/actions";
import {
  LocalDateString,
  OfficeWorkday,
  Workday,
  WorkdayStatus,
  WorkspaceReservation,
} from "@lib/store/src/data/timeslots/apiTypes";
import { getWorkdaysForDate, getWorkspaceReservationsForDate } from "@lib/store/src/data/timeslots/selectors";
import { IconButton } from "@molecules";
import { BookWorkspaceCard, ParkingSelectionCard } from "@organisms";
import { cn } from "@utils";
import { useMemoDate } from "@lib/store/src/ui/hooks/useMemoDate";
import { ErrorObject } from "../types";
import { IconStatusMapping } from "./utils";
import { useReservationDetails } from "@lib/store/src/ui/hooks/timeslots/useReservationDetails";
import { LicensePlatesModalPage } from "src/components/organisms/profile/licensePlates/LicensePlatesModalPage/LicensePlatesModalPage";
import { WorkdayDetailsModal } from "../../WorkdayTile/WorkdayDetailsModal";
import { usePreselectTimeslot } from "@lib/logic";

type Props = {
  disabled: boolean;
  date: string;

  processingFor: WorkdayStatus | null;
  setProcessingFor: Dispatch<SetStateAction<WorkdayStatus | null>>;

  loadingWorkplace: boolean;
  setLoadingWorkplace: Dispatch<SetStateAction<boolean>>;
  loadingParking: boolean;
  setLoadingParking: Dispatch<SetStateAction<boolean>>;

  errorObject: ErrorObject;
  setErrorObject: Dispatch<SetStateAction<ErrorObject>>;

  isIncompleteBooking: boolean;
};

export const QuickOfficeDayButton = ({
  disabled,
  date: dateKey,
  loadingWorkplace,
  setLoadingWorkplace,
  loadingParking,
  setLoadingParking,
  errorObject,
  setErrorObject,
  processingFor,
  setProcessingFor,
  isIncompleteBooking,
}: Props) => {
  const forWorkdayStatus = "OfficeDay" satisfies WorkdayStatus;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { workspaceLocation, parkingLocation } = useAppSelector(getPreferences);
  const parkingSettings = useAppSelector(getParkingSettings);
  const { addPages, openModal } = useModal();
  // When a building is removed from a user's location profile, their preference is not cleared.
  // We don't want the page to crash in those cases.
  const defaultBuildingId = useAppSelector(getDefaultBuildingId);
  const defaultBuildingFromStore = useAppSelector(getDefaultBuilding);
  if (!defaultBuildingFromStore) console.warn("Could not resolve default building", defaultBuildingId);
  const defaultBuilding = defaultBuildingFromStore || {
    id: defaultBuildingId,
    allowWorkdayWithoutWorkspace: true,
  };

  const bookWorkspace = useBookWorkspace();

  const parsedDate = useMemoDate(dateKey);

  // TODO: support multiple (this picks the first)
  const workdayData = useAppSelector(state => getWorkdaysForDate(state, dateKey));
  const workday = workdayData?.data?.at(0);

  // TODO: support multiple (this picks the first)
  const workspaceReservationData = useAppSelector(getWorkspaceReservationsForDate(dateKey));
  const workspaceReservation = workspaceReservationData?.data?.find(r => r.status === "Confirmed");

  const buildingId = workday?.status === "OfficeDay" ? workday.buildingId : defaultBuilding.id;
  const isWorkdaySetInDefaultBuilding = buildingId === defaultBuilding.id;
  const isCreating = processingFor === forWorkdayStatus;
  const hasOptimisticWorkday = workday || isCreating;

  const isUserAllowedToReserveParking = useAppSelector(state =>
    buildingId ? getIsUserAllowedToReserveParking(state, parsedDate, buildingId) : false,
  );

  const defaultParkingLot = useAppSelector(state => getParkingLotById(state, parkingLocation?.parkingLotId || ""));
  // has configured preferences and quota / profiles allow workspace booking on this day

  const defaultBuildingIsClosed = useAppSelector(state =>
    getIsNodeClosed(state, { date: parsedDate, nodeId: defaultBuilding.id }),
  );

  const canUseAutoBookingFlow =
    !!workspaceLocation?.floorId &&
    isWorkdaySetInDefaultBuilding &&
    workspaceLocation.isDeskRequired &&
    !defaultBuildingIsClosed;

  const allowWorkdayOnly = defaultBuilding?.allowWorkdayWithoutWorkspace ?? false;
  const isLicensePlateMandatory = parkingSettings?.licensePlateIsRequired ?? false;

  // Parking
  const parkingReservationByDate = useAppSelector(state => getParkingReservationByDate(state, dateKey));
  const licensePlates = useAppSelector(getLicensePlates);
  const isParkingFeatureVisible = useAppSelector(state => getIsParkingFeatureVisibleForBuilding(state, buildingId));
  // currently only isLicensePlateMandatory is only set ins BE
  const shouldOpenLicensePlateModal = isLicensePlateMandatory && !licensePlates?.length;
  const showParking = hasOptimisticWorkday && !parkingReservationByDate && isParkingFeatureVisible;
  const canBookParking = isUserAllowedToReserveParking && showParking;
  const canUseParkingAutoBookingFlow =
    !!parkingLocation?.parkingLotId &&
    buildingId &&
    defaultParkingLot?.buildingIds.includes(buildingId) &&
    canBookParking;

  const createWorkdayInDefaultBuilding = async () => {
    setProcessingFor(forWorkdayStatus);

    const { success } = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          status: "OfficeDay",
          buildingId: defaultBuilding.id,
          localStart: `${dateKey}T00:00`,
          localEnd: `${dateKey}T23:59`,
        }),
      ),
    );

    setProcessingFor(null);
  };

  const autoWorkspaceBooking = async () => {
    setLoadingWorkplace(true);

    // Clear error state (?)
    setProcessingFor(forWorkdayStatus);
    const errorObjectKey = allowWorkdayOnly ? "workplace" : "workday";
    setErrorObject(p => ({
      ...p,
      [errorObjectKey]: null,
    }));

    const action = workday
      ? withAsyncThunkErrorHandling(() =>
          actions.createWorkspaceReservation({
            localEnd: workday.localEnd,
            localStart: workday.localStart,
            workdayId: workday.id,
          }),
        )
      : withAsyncThunkErrorHandling(() => actions.bookWorkspaceBasedOnPreferences({ dateKey }));

    const result = await dispatch(action);
    const reservation = result.success ? result.result : null;

    // Clear loading stte
    setProcessingFor(null);
    setLoadingWorkplace(false);

    const { floorId, areaId } = workspaceLocation || {};

    if (!reservation) {
      setErrorObject(p => ({
        ...p,
        [errorObjectKey]: t("workplace:CouldntFindASpot"),
      }));
    } else {
      const inRightArea = !areaId || ("areaId" in reservation.workspace && reservation.workspace.areaId === areaId);
      const onRightFloor =
        !floorId || ("floorId" in reservation.workspace && reservation.workspace.floorId === floorId);

      if (!inRightArea || !onRightFloor) {
        setErrorObject(p => ({
          ...p,
          [errorObjectKey]: t("workplace:AlternativePick"),
        }));
      }
    }
  };

  const autoParkingBooking = async () => {
    const { parkingLotId } = parkingLocation || {};
    if (!parkingLotId) return;
    setLoadingParking(true);
    setErrorObject(p => ({
      ...p,
      parking: null,
    }));

    try {
      await dispatch(
        reserveParkingSpotWithPreferredLocation({
          selectedDate: dateKey,
        }),
      ).unwrap();
      setLoadingParking(false);
    } catch (error) {
      setErrorObject(p => ({
        ...p,
        parking: t("parking:CouldntFindASpot"),
      }));
      setLoadingParking(false);
    }
  };

  const openWorkdayModal = useEditWorkday().openModal;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    if (workday) {
      openWorkdayModal(workday, dateKey);
    } else if (canUseAutoBookingFlow && !allowWorkdayOnly) {
      autoWorkspaceBooking();
    } else if (allowWorkdayOnly) {
      createWorkdayInDefaultBuilding();
    } else {
      openWorkdayModal(null, dateKey);
    }
  };
  const preselectTimeslot = usePreselectTimeslot({ dateKey });

  const handleAddWorkspace = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (canUseAutoBookingFlow) {
      autoWorkspaceBooking();
    } else if (workday?.status === "OfficeDay") {
      preselectTimeslot();
      dispatch(selectBuilding(workday.buildingId));
      bookWorkspace.openModal(workday);
    } else {
      console.error("add workspace button was clicked while it shouldn't have been supported");
    }
    e.preventDefault();
  };

  const handleAddParking = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!canBookParking || !buildingId) return;

    if (canUseParkingAutoBookingFlow) {
      autoParkingBooking();
    } else {
      if (shouldOpenLicensePlateModal) {
        addPages([
          <LicensePlatesModalPage
            date={dateKey}
            buildingId={buildingId}
          />,
        ]);
      } else {
        addPages([
          <ParkingSelectionCard
            date={dateKey}
            buildingId={buildingId}
          />,
        ]);
      }

      openModal();
    }
  };

  useEffect(
    function resetErrorWhenWorkspaceIsBookedOutside() {
      if (errorObject.workplace && workspaceReservation) {
        setErrorObject(p => ({
          ...p,
          workday: null,
          workplace: null,
        }));
      }
    },
    [workspaceReservation, errorObject.workplace, setErrorObject],
  );

  useEffect(
    function resetErrorWhenParkingIsBookedOutside() {
      if (errorObject.parking && parkingReservationByDate) {
        setErrorObject(p => ({
          ...p,
          parking: null,
        }));
      }
    },
    [parkingReservationByDate, errorObject.parking, setErrorObject],
  );

  const nrOfReservations = workspaceReservationData?.data?.length ?? -1;
  const workspaceLabel =
    nrOfReservations > 1
      ? nrOfReservations.toString()
      : workspaceReservation?.workspace.nodeType === "Area" || workspaceReservation?.workspace.nodeType === "Desk"
        ? workspaceReservation.workspace.areaName
        : workspaceReservation?.workspace.nodeType === "Floor"
          ? workspaceReservation.workspace.floorName
          : (workspaceReservation?.workspace.buildingName ?? "");

  return (
    <>
      <IconButton
        className={cn("shrink-0", {
          "bg-energizing-yellow-500*": hasOptimisticWorkday && !disabled,
        })}
        disabled={disabled || !!errorObject.workday}
        iconButton="tertiary"
        icon={IconStatusMapping[forWorkdayStatus]}
        onClick={handleClick}
      />

      {!loadingWorkplace && hasOptimisticWorkday && !workspaceReservation && (
        <button
          disabled={disabled || !!errorObject.workplace}
          className="group transition-colors bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.06] disabled:pointer-events-none shadow-mapiq-base rounded-md p-2 flex gap-1.5 items-center order-1"
          onClick={handleAddWorkspace}>
          <Icon
            icon="plus"
            color="currentColor"
            className={"size-4 group-disabled:text-mapiq-black-400"}
          />
          <Icon
            icon="desk"
            color="currentColor"
            className={"size-4 group-disabled:text-mapiq-black-400"}
          />
        </button>
      )}

      {!loadingWorkplace && workspaceReservation && (
        <button
          disabled={disabled}
          className="group bg-mapiq-black-800*/[0.03] rounded-md p-2 flex gap-1.5 max-w-[14ch] items-center order-1">
          <span className="shrink-0 relative ">
            <span
              className={cn(
                "block absolute top-0 right-0 bg-functional-red size-1.5 rounded-full border-white opacity-0",
                {
                  "animate-in fade-in fill-mode-forwards": errorObject.workplace,
                  "animate-out fade-out fill-mode-forwards": !errorObject.workplace,
                },
              )}
            />
            <Icon
              icon="desk"
              color="currentColor"
              className={"size-4 group-disabled:text-mapiq-black-400"}
            />
          </span>
          {!showParking && !parkingReservationByDate && (
            <p
              className="truncate leading-none"
              title={workspaceLabel}>
              {workspaceLabel}
            </p>
          )}
          <span className="size-4 bg-connecting-green-500* rounded-[4px] flex justify-center items-center shrink-0  group-disabled:opacity-50">
            <Icon
              icon="check"
              color="white"
              className="size-3"
            />
          </span>
        </button>
      )}

      {!loadingParking && showParking && hasOptimisticWorkday && !parkingReservationByDate && !isIncompleteBooking && (
        <button
          disabled={disabled || !canBookParking || !!errorObject.parking}
          className="group transition-colors bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.06] disabled:pointer-events-none shadow-mapiq-base rounded-md p-2 flex gap-1.5 items-center order-2"
          onClick={handleAddParking}>
          <Icon
            icon="plus"
            color="currentColor"
            className={"size-4 group-disabled:!text-mapiq-black-400"}
          />
          <Icon
            icon="car"
            color="currentColor"
            className={"size-4 group-disabled:!text-mapiq-black-400"}
          />
        </button>
      )}

      {!loadingParking && parkingReservationByDate && (
        <button
          disabled={disabled}
          className="group bg-mapiq-black-800*/[0.03] rounded-md p-2 flex gap-1.5 max-w-[14ch] items-center order-2">
          <span className="shrink-0 relative">
            <span
              className={cn(
                "block absolute top-0 right-0 bg-functional-red size-1.5 rounded-full border-white opacity-0",
                {
                  "animate-in fade-in fill-mode-forwards": errorObject.parking,
                  "animate-out fade-out fill-mode-forwards": !errorObject.parking,
                },
              )}
            />
            <Icon
              icon="car"
              color="currentColor"
              className={"size-4 group-disabled:text-mapiq-black-400"}
            />
          </span>
          <span className="size-4 bg-connecting-green-500* rounded-[4px] flex justify-center items-center shrink-0 group-disabled:opacity-50">
            <Icon
              icon="check"
              color="white"
              className="size-3"
            />
          </span>
        </button>
      )}

      {(loadingWorkplace || loadingParking) && (
        <div
          className={cn("bg-mapiq-black-800*/[0.03] rounded-md p-2 flex gap-1.5 items-center order-3", {
            "order-1": loadingWorkplace && !loadingParking,
          })}>
          <Loader className="size-4" />
          <small className="truncate leading-none">{t("workplace:FindingASpot")}</small>
        </div>
      )}
    </>
  );
};

export const useEditWorkday = () => {
  const features = useOverridableFeatureFlags();
  const { setModalPages, openModal } = useModal();

  return {
    openModal: (workday: Workday | null, date: LocalDateString) => {
      setModalPages([
        <WorkdayDetailsModal
          selectedDate={date}
          workday={workday ?? undefined}
        />,
      ]);

      openModal();
    },
  };
};

export const useBookWorkspace = () => {
  const { setModalPages, openModal } = useModal();

  return {
    openModal: (workday: OfficeWorkday) => {
      setModalPages([<BookWorkspaceCard workday={workday} />]);
      openModal();
    },
  };
};

export const useEditWorkspaceReservation = (workspaceReservation: WorkspaceReservation) => {
  const { addPages, openModal } = useModal();
  const details = useReservationDetails(workspaceReservation);

  return {
    openModal: (workday: OfficeWorkday) => {
      if (details.status !== "fulfilled") {
        // TODO: ensure this in a better way
        alert("Please wait for details to be loaded");
        return;
      }

      addPages([
        <BookWorkspaceCard
          workday={workday}
          workspaceReservation={workspaceReservation}
          members={details.data}
        />,
      ]);
      openModal();
    },
  };
};
